import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AppRole } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { debugLog, debugWarn } from '@/lib/debugLogger';
import { isSyncRedirectLoop } from '@/lib/redirectLoopGuard';
import { requestSpaRedirect } from '@/lib/navigationControl';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';
import { isDemoBlockedRoute } from '@/lib/demoGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
}

/**
 * ProtectedRoute — Single authority for authenticated route access.
 * 
 * ARCHITECTURAL RULE: This is the ONLY component that redirects
 * unauthenticated users to /auth. No other component may do this.
 * 
 * Uses useEffect + setTimeout for navigation to prevent double-pushState
 * caused by React StrictMode's double-mount behavior.
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, role, loading, hasAccess, isDemo } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [roleTimeoutExpired, setRoleTimeoutExpired] = useState(false);
  const [authRedirectReady, setAuthRedirectReady] = useState(false);
  const hasNavigatedRef = useRef(false);
  const authFallbackUrl = buildPathWithPreviewToken('/auth');
  const accessDeniedUrl = buildPathWithPreviewToken('/acesso-negado');

  // Reset navigation guard when user changes
  useEffect(() => {
    hasNavigatedRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (!user || role || loading) {
      setRoleTimeoutExpired(false);
      return;
    }

    debugLog('auth', '[NetUno:AuthFlow] ProtectedRoute::role_wait_start', { path: location.pathname });

    const timer = window.setTimeout(() => {
      debugWarn('auth', '[NetUno:AuthFlow] ProtectedRoute::role_timeout_expired');
      setRoleTimeoutExpired(true);
    }, 8_000);

    return () => window.clearTimeout(timer);
  }, [user, role, loading, location.pathname]);

  useEffect(() => {
    if (loading || user) {
      setAuthRedirectReady(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setAuthRedirectReady(true);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [loading, user, location.pathname]);

  // Redirect unauthenticated users to /auth via useEffect (StrictMode safe)
  useEffect(() => {
    if (loading || user || !authRedirectReady || hasNavigatedRef.current) return;

    if (isSyncRedirectLoop('ProtectedRoute') || !requestSpaRedirect('ProtectedRoute::redirect_auth', authFallbackUrl)) {
      debugWarn('auth', '[NetUno:AuthFlow] ProtectedRoute::loop_broken');
      return;
    }

    const timer = window.setTimeout(() => {
      if (hasNavigatedRef.current) return;
      hasNavigatedRef.current = true;

      console.log('[NetUno:NAVIGATION]', `${location.pathname}${location.search}`, '→', authFallbackUrl, {
        source: 'ProtectedRoute::redirect_auth',
        hasUser: Boolean(user),
        hasRole: Boolean(role),
        loading,
        at: new Date().toISOString(),
      });

      debugLog('auth', '[NetUno:AuthFlow] Router::redirect_auth', { from: location.pathname });
      navigate(authFallbackUrl, { state: { from: location }, replace: true });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [loading, user, authRedirectReady, authFallbackUrl, location, navigate, role]);

  // Redirect unauthorized users to /acesso-negado via useEffect (StrictMode safe)
  useEffect(() => {
    if (loading || !user || !role || hasNavigatedRef.current) return;
    if (!requiredRoles || requiredRoles.length === 0) return;
    if (hasAccess(requiredRoles)) return;

    debugLog('auth', '[NetUno:AuthFlow] ProtectedRoute::access_denied', {
      role,
      requiredRoles,
      path: location.pathname,
    });

    if (!requestSpaRedirect('ProtectedRoute::access_denied', accessDeniedUrl)) return;

    const timer = window.setTimeout(() => {
      if (hasNavigatedRef.current) return;
      hasNavigatedRef.current = true;

      console.log('[NetUno:NAVIGATION]', `${location.pathname}${location.search}`, '→', accessDeniedUrl, {
        source: 'ProtectedRoute::access_denied',
        role,
        requiredRoles,
        at: new Date().toISOString(),
      });

      navigate(accessDeniedUrl, { replace: true });
    }, 150);

    return () => window.clearTimeout(timer);
  }, [loading, user, role, requiredRoles, hasAccess, accessDeniedUrl, location, navigate]);

  // 1. Auth system still loading → spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // 2. No user → show spinner while redirect timer fires
  if (!user) {
    if (!authRedirectReady) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Verificando sessão…</p>
        <a 
          href={authFallbackUrl} 
          className="text-primary underline text-sm mt-2"
          onClick={(e) => {
            e.preventDefault();
            window.location.href = authFallbackUrl;
          }}
        >
          Ir para login
        </a>
      </div>
    );
  }

  // 3. User exists but role not loaded yet → wait with loader
  if (!role) {
    if (roleTimeoutExpired) {
      debugWarn('auth', '[NetUno:AuthFlow] ProtectedRoute::no_role_after_timeout');
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
          <p className="text-sm text-muted-foreground">Não foi possível carregar permissões.</p>
          <a 
            href={authFallbackUrl} 
            className="text-primary underline text-sm"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = authFallbackUrl;
            }}
          >
            Ir para login
          </a>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">Carregando permissões…</p>
      </div>
    );
  }

  // 4. Role loaded — check access (redirect handled by useEffect above)
  if (requiredRoles && requiredRoles.length > 0 && !hasAccess(requiredRoles)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // 4b. Demo-user route guard — strictly additive, only fires when isDemo === true.
  // Does NOT touch MODULE_VIEW_PERMISSIONS / hasAccess.
  if (isDemo && isDemoBlockedRoute(location.pathname)) {
    debugWarn('auth', '[NetUno:AuthFlow] ProtectedRoute::demo_blocked_route', {
      path: location.pathname,
    });
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3 p-6 text-center">
        <p className="text-lg font-semibold">Área indisponível no modo demonstração</p>
        <p className="text-sm text-muted-foreground max-w-md">
          Esta seção administrativa não está disponível para usuários de demonstração.
        </p>
        <a href={buildPathWithPreviewToken('/')} className="text-primary underline text-sm">
          Voltar ao início
        </a>
      </div>
    );
  }


  // 5. Authorized
  debugLog('auth', '[NetUno:AuthFlow] ProtectedRoute::authorized', {
    role,
    path: location.pathname,
  });
  return <>{children}</>;
}
